import type { ModalHandle, ModalOptions } from '../modal';
import type { AdminUser, AdminInvoice, AdminApiKey, AdminPaymentConfig, AdminFullPaymentConfig, CmsLegal, CmsFaq, CmsNews, CmsSocial } from '../admin-api';

/** One row of the social builder. `id` is absent until the row has been saved. */
export interface SocialDraftRow {
  key: string;
  id?: string;
  platform: string;
  url: string;
  active: boolean;
}

export type PaneId = 'overview' | 'players' | 'invoices' | 'cms' | 'company' | 'meta' | 'keys' | 'payments';

/**
 * Everything the panes read and write, in one object on the screen's closure.
 *
 * Deliberately NOT a store or any reactive primitive: no other screen in this
 * app has one, and the only values that update while a user is mid-interaction
 * live inside a modal, which patches its own text nodes (see `modal.ts`).
 */
export interface AdminState {
  activePane: PaneId;
  busy: boolean;
  overview: {
    stats: Record<string, unknown> | null;
    serverStatus: Record<string, unknown> | null;
    paymentConfig: AdminPaymentConfig | null;
    /** Raw `game_settings` strings — the source of truth for the feature switches. */
    settings: Record<string, string> | null;
  };
  players: { items: AdminUser[]; search: string };
  invoices: { items: AdminInvoice[] };
  cms: {
    tab: 'legal' | 'faq' | 'news' | 'social';
    legal: CmsLegal[];
    faq: CmsFaq[];
    news: CmsNews[];
    /** Social links as stored on the server. */
    social: CmsSocial[];
    /** The social builder's working copy, in display order; saved as a whole. */
    socialDraft: SocialDraftRow[];
  };
  company: Record<string, string>;
  meta: Record<string, string>;
  keys: AdminApiKey[];
  payments: { config: AdminFullPaymentConfig | null };
}

export function initialAdminState(): AdminState {
  return {
    activePane: 'overview',
    busy: false,
    overview: { stats: null, serverStatus: null, paymentConfig: null, settings: null },
    players: { items: [], search: '' },
    invoices: { items: [] },
    cms: { tab: 'legal', legal: [], faq: [], news: [], social: [], socialDraft: [] },
    company: {},
    meta: {},
    keys: [],
    payments: { config: null },
  };
}

/** What the shell hands each pane. */
export interface PaneCtx {
  state: AdminState;
  /** Re-fetch a pane's data and repaint. */
  reload(pane: PaneId): Promise<void>;
  /** Serialises mutations and surfaces failures as a toast. */
  withBusy(fn: () => Promise<void>, failure: string): Promise<void>;
  /** Opens a dialog, closing any already-open one. Tracked for teardown. */
  openModal(options: ModalOptions): ModalHandle;
  /** Repaint the current pane from state, without re-fetching. */
  repaint(): void;
  disposed(): boolean;
}

export interface PaneModule {
  id: PaneId;
  label: string;
  html(ctx: PaneCtx): string;
  load?(ctx: PaneCtx): Promise<void>;
  /** Return true when the action was handled. */
  onAction?(action: string, el: HTMLElement, ctx: PaneCtx): boolean;
  /** Return true when the submit was handled. */
  onSubmit?(kind: string, form: HTMLFormElement, ctx: PaneCtx): boolean;
  /** Delegated from the screen root. Return true when handled. */
  onPointerDown?(e: PointerEvent, ctx: PaneCtx): boolean;
  onKeyDown?(e: KeyboardEvent, ctx: PaneCtx): boolean;
  onChange?(el: HTMLElement, ctx: PaneCtx): boolean;
}
